import { calgaryYmdString } from "./deadline";
import type {
  FilterState,
  HearingStatus,
  HearingType,
} from "./filter-state";
import type { Hearing } from "./types";

// Pure client- and server-safe filter applied to the rendered-domain
// `Hearing[]` after the unfiltered upstream fetch. Used by both the
// SSR pass (in `page.tsx`) and the client filter UI (the
// `HearingsFilters` + `HearingsList` pair) so the first paint and
// subsequent interactions agree on the visible set.
//
// Hearing-date windowing is done by Calgary-local calendar day. The
// user sets `hearingAfter` / `hearingBefore` as `YYYY-MM-DD` strings;
// we project the hearing's UTC meeting time onto the Calgary day it
// falls on (via `calgaryYmdString`) and compare lexicographically.
// This is DST-safe and avoids the per-render trap of fixing an offset
// like `-06:00` that's only correct half the year.
export function applyFilters(
  hearings: Hearing[],
  state: FilterState,
  now: Date = new Date(),
): Hearing[] {
  const q = state.q.trim().toLowerCase();
  const community = state.community?.toLowerCase() ?? null;
  const wardSet = state.wards.length > 0 ? new Set(state.wards) : null;
  const statusSet =
    state.statuses.length > 0 ? new Set<HearingStatus>(state.statuses) : null;
  const typeSet =
    state.types.length > 0 ? new Set<HearingType>(state.types) : null;
  const after = state.hearingAfter;
  const before = state.hearingBefore;

  return hearings.filter((h) => {
    if (community && !h.communities.some((c) => c.toLowerCase() === community)) {
      return false;
    }

    if (wardSet && !h.wards.some((w) => wardSet.has(w))) {
      return false;
    }

    if (statusSet && !statusSet.has(deriveStatus(h, now))) {
      return false;
    }

    if (typeSet) {
      const types = deriveTypes(h);
      let any = false;
      for (const t of types) {
        if (typeSet.has(t)) {
          any = true;
          break;
        }
      }
      if (!any) return false;
    }

    if (after != null || before != null) {
      if (!h.meeting.meetingTimeUtc) return false;
      const ts = Date.parse(h.meeting.meetingTimeUtc);
      if (!Number.isFinite(ts)) return false;
      const dlYmd = calgaryYmdString(new Date(ts));
      if (after != null && dlYmd < after) return false;
      if (before != null && dlYmd > before) return false;
    }

    if (q && !matchesQuery(h, q)) {
      return false;
    }

    return true;
  });
}

function matchesQuery(h: Hearing, q: string): boolean {
  if (h.title.toLowerCase().includes(q)) return true;
  if (h.description && h.description.toLowerCase().includes(q)) return true;
  if (h.communities.some((c) => c.toLowerCase().includes(q))) return true;
  if (h.bylawNumbers.some((b) => b.toLowerCase().includes(q))) return true;
  if (h.locCodes.some((l) => l.toLowerCase().includes(q))) return true;
  return false;
}

// Submission-window status, projected onto the canonical 3-bucket scale
// the filter chips use. `closing` is the 48h before the deadline window;
// outside that window (and before the deadline) the hearing is `open`;
// once the deadline has passed it is `closed`. A hearing with no
// declared submission deadline is treated as `open` (the upstream omits
// the deadline for non-submission-eligible items, but the page still
// shows them so the participation funnel stays whole).
const CLOSING_WINDOW_MS = 48 * 60 * 60 * 1000;

export function deriveStatus(h: Hearing, now: Date = new Date()): HearingStatus {
  if (!h.submissionDeadlineUtc) return "open";
  const dl = Date.parse(h.submissionDeadlineUtc);
  if (!Number.isFinite(dl)) return "open";
  const diff = dl - now.getTime();
  if (diff <= 0) return "closed";
  if (diff <= CLOSING_WINDOW_MS) return "closing";
  return "open";
}

// Derived hearing-type set. A single hearing can match multiple types
// (e.g. an "Outline plan + land use" amendment); the chip filter uses
// OR-intersection across the user's selected types. Heuristic mirrors
// `inferMeta` in HearingsList so the chips and the row meta line stay
// in agreement on what counts as which type.
//
//   - `outline_plan` — title contains "outline plan"
//   - `land_use`     — title contains "land use"
//   - `policy`       — title contains "policy" OR no district codes
//                      either side (statutory plan amendment)
export function deriveTypes(h: Hearing): Set<HearingType> {
  const t = h.title.toLowerCase();
  const out = new Set<HearingType>();
  if (t.includes("outline plan")) out.add("outline_plan");
  if (t.includes("land use")) out.add("land_use");
  if (
    t.includes("policy") ||
    (h.fromDistricts.length === 0 && h.toDistricts.length === 0)
  ) {
    out.add("policy");
  }
  return out;
}

export function uniqueCommunities(hearings: Hearing[]): string[] {
  const set = new Set<string>();
  for (const h of hearings) {
    for (const c of h.communities) set.add(c);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function uniqueWards(hearings: Hearing[]): number[] {
  const set = new Set<number>();
  for (const h of hearings) {
    for (const w of h.wards) set.add(w);
  }
  return [...set].sort((a, b) => a - b);
}

// Counts per chip facet — used by HearingsFilters to render `(N)`
// counts next to each option, so the reader sees up-front how many
// hearings each chip would match. Counts are computed against the
// *unfiltered* set deliberately: showing pressed-state counts that
// shrink as the user toggles other facets makes the UI feel jumpy.
export type FacetCounts = {
  ward: Map<number, number>;
  status: Record<HearingStatus, number>;
  type: Record<HearingType, number>;
};

export function facetCounts(
  hearings: Hearing[],
  now: Date = new Date(),
): FacetCounts {
  const ward = new Map<number, number>();
  const status: Record<HearingStatus, number> = {
    open: 0,
    closing: 0,
    closed: 0,
  };
  const type: Record<HearingType, number> = {
    land_use: 0,
    outline_plan: 0,
    policy: 0,
  };

  for (const h of hearings) {
    for (const w of h.wards) ward.set(w, (ward.get(w) ?? 0) + 1);
    status[deriveStatus(h, now)] += 1;
    for (const t of deriveTypes(h)) type[t] += 1;
  }

  return { ward, status, type };
}
